import type { NotificationChannel } from './types'

/** Draft selection keyed by channel, seeded from the server's effective state. */
export function initialSelection(channels: NotificationChannel[]): Record<string, boolean> {
  return Object.fromEntries(channels.map((c) => [c.channel, c.is_enabled]))
}

/** Only the channels whose draft differs from the saved state, or that were never explicitly chosen. */
export function changedChannels(
  channels: NotificationChannel[],
  draft: Record<string, boolean>,
): Record<string, boolean> {
  const changed: Record<string, boolean> = {}
  for (const c of channels) {
    const value = draft[c.channel]
    if (value === undefined) continue
    if (value !== c.is_enabled || !c.is_explicit) changed[c.channel] = value
  }
  return changed
}

/**
 * Blocking error for the draft, or null. Turning on a channel that can't
 * deliver (e.g. email with no address on the account) is refused here and
 * by the API; turning it off is always allowed.
 */
export function selectionError(channels: NotificationChannel[], draft: Record<string, boolean>): string | null {
  for (const c of channels) {
    if (draft[c.channel] && !c.available) return `${c.label} can't be turned on: ${c.note}`
  }
  return null
}

/** Non-blocking warning: no channel selected means no alarm notifications at all. */
export function selectionWarning(channels: NotificationChannel[], draft: Record<string, boolean>): string | null {
  if (channels.length > 0 && channels.every((c) => !draft[c.channel])) {
    return "No channel selected — you won't receive any alarm notifications. Your subscriptions are kept."
  }
  return null
}
